/**
 * TranscriptionScenariosAdmin Component
 *
 * Purpose: Admin interface for managing transcription scenarios
 *
 * Features:
 * - View scenario list with filtering (session_type, active status)
 * - Create/edit scenarios with speaker roles and session type mapping
 * - Clone global system scenarios for airline customization
 * - Activate/deactivate scenarios
 * - Delete org-specific scenarios (with confirmation)
 *
 * Sprint: 5 (Transcription Pipeline)
 * Author: Edward Berger + AI Assistant
 * Date: 2026-03-22
 */

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useAirline } from '../../contexts/AirlineContext';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  Loader,
  Copy,
  Globe,
  Mic,
} from 'lucide-react';

// =====================================================================
// Constants
// =====================================================================

const SYSTEM_ORG_ID = '00000000-0000-0000-0000-000000000000';

const PREDEFINED_ROLES = [
  'Captain',
  'First Officer',
  'Instructor',
  'Examiner',
  'Check Airman',
  'Observer',
  'Trainee',
  'Voice Alert',
];

// =====================================================================
// Types & Interfaces
// =====================================================================

interface TranscriptionScenario {
  id: string;
  org_id: string;
  code: string;
  name: string;
  description: string | null;
  context_description: string | null;
  transcription_prompt: string | null;
  speaker_roles: string[];
  speaker_role_descriptions: Record<string, string> | null;
  min_speakers: number;
  max_speakers: number;
  is_active: boolean;
  sort_order: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface SessionType {
  id: string;
  code: string;
  name: string;
}

interface ScenarioForm {
  name: string;
  code: string;
  description: string;
  context_description: string;
  transcription_prompt: string;
  speaker_roles: string[];
  speaker_role_descriptions: Record<string, string>;
  min_speakers: number;
  max_speakers: number;
  session_type_ids: string[];
  is_active: boolean;
}

interface FilterState {
  session_type: string;
  status: string;
}

// =====================================================================
// Main Component
// =====================================================================

export const TranscriptionScenariosAdmin: React.FC = () => {
  const { user } = useAuth();
  const { selectedAirline } = useAirline();

  // State
  const [scenarios, setScenarios] = useState<TranscriptionScenario[]>([]);
  const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);
  const [junctionMap, setJunctionMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({ session_type: 'all', status: 'active' });
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState<TranscriptionScenario | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState<ScenarioForm>({
    name: '', code: '', description: '', context_description: '', transcription_prompt: '',
    speaker_roles: [], speaker_role_descriptions: {},
    min_speakers: 2, max_speakers: 4, session_type_ids: [], is_active: true,
  });
  const [customRoleInput, setCustomRoleInput] = useState('');

  // Derive organization_id from the selected airline (authoritative source)
  useEffect(() => {
    if (selectedAirline?.airline_id) {
      setOrganizationId(selectedAirline.airline_id);
    }
  }, [selectedAirline?.airline_id]);

  // ===================================================================
  // Load data
  // ===================================================================

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch scenarios
      let query = supabase
        .from('transcription_scenarios')
        .select('*')
        .order('sort_order', { ascending: true });

      if (filters.status === 'active') query = query.eq('is_active', true);
      else if (filters.status === 'inactive') query = query.eq('is_active', false);

      const [scenariosRes, typesRes, junctionRes] = await Promise.all([
        query,
        supabase.from('session_types').select('id, code, name').order('name'),
        supabase.from('transcription_scenario_session_types').select('scenario_id, session_type_id'),
      ]);

      if (scenariosRes.error) {
        console.error('[TranscriptionScenariosAdmin] Load scenarios error:', scenariosRes.error);
        return;
      }
      if (typesRes.error) {
        console.error('[TranscriptionScenariosAdmin] Load session types error:', typesRes.error);
        return;
      }

      setScenarios(scenariosRes.data || []);
      setSessionTypes(typesRes.data || []);

      // Build junction map: scenario_id -> session_type_id[]
      const map: Record<string, string[]> = {};
      for (const row of junctionRes.data || []) {
        const key = row.scenario_id;
        if (!map[key]) map[key] = [];
        map[key]!.push(row.session_type_id);
      }
      setJunctionMap(map);
    } catch (error) {
      console.error('[TranscriptionScenariosAdmin] Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { loadData(); }, [loadData]);

  // ===================================================================
  // Filtered scenarios
  // ===================================================================

  const filteredScenarios = scenarios.filter((s) => {
    const matchesSearch =
      searchQuery === '' ||
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.code.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSessionType =
      filters.session_type === 'all' ||
      (junctionMap[s.id] || []).includes(filters.session_type);

    return matchesSearch && matchesSessionType;
  });

  // ===================================================================
  // Helpers
  // ===================================================================

  const isSystemScenario = (s: TranscriptionScenario): boolean => s.org_id === SYSTEM_ORG_ID;

  const sessionTypeNameById = (id: string): string =>
    sessionTypes.find((t) => t.id === id)?.name || id;

  const generateCode = (name: string): string =>
    name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const resetForm = () => {
    setForm({
      name: '', code: '', description: '', context_description: '', transcription_prompt: '',
      speaker_roles: [], speaker_role_descriptions: {},
      min_speakers: 2, max_speakers: 4, session_type_ids: [], is_active: true,
    });
    setCustomRoleInput('');
    setEditingScenario(null);
  };

  // ===================================================================
  // Speaker role management
  // ===================================================================

  const addRole = (role: string) => {
    if (!role.trim() || form.speaker_roles.includes(role.trim())) return;
    const trimmed = role.trim();
    setForm((prev) => ({
      ...prev,
      speaker_roles: [...prev.speaker_roles, trimmed],
      speaker_role_descriptions: { ...prev.speaker_role_descriptions, [trimmed]: '' },
    }));
  };

  const removeRole = (role: string) => {
    setForm((prev) => {
      const descs = { ...prev.speaker_role_descriptions };
      delete descs[role];
      return {
        ...prev,
        speaker_roles: prev.speaker_roles.filter((r) => r !== role),
        speaker_role_descriptions: descs,
      };
    });
  };

  // ===================================================================
  // CRUD
  // ===================================================================

  const handleSave = async () => {
    if (!user || !organizationId) {
      alert('Please ensure you are logged in and your organization is loaded.');
      return;
    }
    if (!form.name.trim() || !form.code.trim()) {
      alert('Name and Code are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        code: form.code,
        description: form.description || null,
        context_description: form.context_description || null,
        transcription_prompt: form.transcription_prompt || null,
        speaker_roles: form.speaker_roles,
        speaker_role_descriptions: form.speaker_role_descriptions,
        min_speakers: form.min_speakers,
        max_speakers: Math.max(form.max_speakers, form.min_speakers),
        is_active: form.is_active,
      };

      if (editingScenario) {
        // UPDATE
        const { error } = await supabase
          .from('transcription_scenarios')
          .update(payload)
          .eq('id', editingScenario.id);
        if (error) { alert('Failed to update scenario.'); console.error(error); return; }

        // Replace junction entries
        await supabase
          .from('transcription_scenario_session_types')
          .delete()
          .eq('scenario_id', editingScenario.id);
        if (form.session_type_ids.length > 0) {
          await supabase.from('transcription_scenario_session_types').insert(
            form.session_type_ids.map((stId) => ({
              scenario_id: editingScenario.id,
              session_type_id: stId,
            }))
          );
        }
        alert(`Scenario "${form.name}" updated successfully!`);
      } else {
        // CREATE
        const { data, error } = await supabase
          .from('transcription_scenarios')
          .insert({ ...payload, org_id: organizationId, created_by: user.id })
          .select('id')
          .single();
        if (error || !data) { alert('Failed to create scenario.'); console.error(error); return; }

        if (form.session_type_ids.length > 0) {
          await supabase.from('transcription_scenario_session_types').insert(
            form.session_type_ids.map((stId) => ({
              scenario_id: data.id,
              session_type_id: stId,
            }))
          );
        }
        alert(`Scenario "${form.name}" created successfully!`);
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('[TranscriptionScenariosAdmin] Save error:', error);
      alert('An unexpected error occurred.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scenario: TranscriptionScenario) => {
    if (!confirm(`Delete scenario "${scenario.name}"? This action cannot be undone.`)) return;
    try {
      const { error } = await supabase
        .from('transcription_scenarios')
        .delete()
        .eq('id', scenario.id);
      if (error) { alert('Failed to delete scenario.'); console.error(error); return; }
      alert(`Scenario "${scenario.name}" deleted successfully!`);
      loadData();
    } catch (error) {
      console.error('[TranscriptionScenariosAdmin] Delete error:', error);
    }
  };

  const handleToggleActive = async (scenario: TranscriptionScenario, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from('transcription_scenarios')
        .update({ is_active: !scenario.is_active })
        .eq('id', scenario.id);
      if (error) { alert('Failed to toggle status.'); console.error(error); return; }
      loadData();
    } catch (error) {
      console.error('[TranscriptionScenariosAdmin] Toggle error:', error);
    }
  };

  const handleClone = async (scenario: TranscriptionScenario) => {
    if (!user || !organizationId) {
      alert('Please ensure you are logged in.');
      return;
    }
    if (!confirm(`Clone "${scenario.name}" as an organization-specific scenario?`)) return;
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('transcription_scenarios')
        .insert({
          name: `${scenario.name} (Custom)`,
          code: `${scenario.code}_custom`,
          description: scenario.description,
          context_description: scenario.context_description,
          transcription_prompt: scenario.transcription_prompt,
          speaker_roles: scenario.speaker_roles,
          speaker_role_descriptions: scenario.speaker_role_descriptions,
          min_speakers: scenario.min_speakers,
          max_speakers: scenario.max_speakers,
          is_active: false,
          org_id: organizationId,
          created_by: user.id,
        })
        .select('id')
        .single();
      if (error || !data) { alert('Failed to clone scenario.'); console.error(error); return; }

      // Clone junction entries
      const existingTypes = junctionMap[scenario.id] || [];
      if (existingTypes.length > 0) {
        await supabase.from('transcription_scenario_session_types').insert(
          existingTypes.map((stId) => ({ scenario_id: data.id, session_type_id: stId }))
        );
      }
      alert(`Scenario cloned successfully! You can now customize "${scenario.name} (Custom)".`);
      loadData();
    } catch (error) {
      console.error('[TranscriptionScenariosAdmin] Clone error:', error);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (scenario: TranscriptionScenario) => {
    setEditingScenario(scenario);
    setForm({
      name: scenario.name,
      code: scenario.code,
      description: scenario.description || '',
      context_description: scenario.context_description || '',
      transcription_prompt: scenario.transcription_prompt || '',
      speaker_roles: scenario.speaker_roles || [],
      speaker_role_descriptions: (scenario.speaker_role_descriptions as Record<string, string>) || {},
      min_speakers: scenario.min_speakers,
      max_speakers: scenario.max_speakers,
      session_type_ids: junctionMap[scenario.id] || [],
      is_active: scenario.is_active,
    });
    setShowModal(true);
  };

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy">Transcription Scenarios</h2>
          <p className="text-steel mt-1">
            Manage speaker roles and context for transcription prompts
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition"
        >
          <Plus size={20} />
          <span>Create Scenario</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel" size={20} />
            <input
              type="text"
              placeholder="Search by name or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-navy placeholder-steel/50 border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
            />
          </div>
          <select
            value={filters.session_type}
            onChange={(e) => setFilters({ ...filters, session_type: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Session Types</option>
            {sessionTypes.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Scenario List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-orca-elevate" size={32} />
        </div>
      ) : filteredScenarios.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <Mic className="mx-auto text-steel/50" size={48} />
          <p className="text-steel mt-4">No transcription scenarios found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="w-full">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-2 py-2 text-left text-sm">Name</th>
                <th className="px-2 py-2 text-left text-sm">Code</th>
                <th className="px-2 py-2 text-left text-sm">Speaker Roles</th>
                <th className="px-2 py-2 text-left text-sm">Speakers</th>
                <th className="px-2 py-2 text-left text-sm">Session Types</th>
                <th className="px-2 py-2 text-center text-sm">Prompt</th>
                <th className="px-2 py-2 text-center text-sm">Status</th>
                <th className="px-2 py-2 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredScenarios.map((scenario) => (
                <tr key={scenario.id} className="border-b border-steel/20 hover:bg-white transition">
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm">{scenario.name}</p>
                      {isSystemScenario(scenario) && (
                        <span
                          className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold"
                          title="Global system scenario - Read-only"
                        >
                          <Globe size={12} />
                          GLOBAL
                        </span>
                      )}
                    </div>
                    {scenario.description && (
                      <p className="text-xs text-steel mt-0.5 line-clamp-1">{scenario.description}</p>
                    )}
                  </td>
                  <td className="px-2 py-2 text-steel text-sm font-mono">{scenario.code}</td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(scenario.speaker_roles || []).map((role) => (
                        <span key={role} className="px-1.5 py-0.5 bg-sky-100 text-sky-800 rounded text-xs">
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-steel text-sm">
                    {scenario.min_speakers}-{scenario.max_speakers}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(junctionMap[scenario.id] || []).map((stId) => (
                        <span key={stId} className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">
                          {sessionTypeNameById(stId)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    {scenario.transcription_prompt ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-semibold">
                        Custom
                      </span>
                    ) : (
                      <span className="text-steel/40 text-xs">Default</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    {isSystemScenario(scenario) ? (
                      <span title="Global scenarios are always active">
                        <CheckCircle className="text-blue-600 inline" size={18} />
                      </span>
                    ) : (
                      <button
                        onClick={(e) => handleToggleActive(scenario, e)}
                        className="p-1 hover:bg-steel/10 rounded"
                        title={scenario.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {scenario.is_active ? (
                          <CheckCircle className="text-green-600" size={18} />
                        ) : (
                          <XCircle className="text-red-600" size={18} />
                        )}
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      {isSystemScenario(scenario) ? (
                        <button
                          onClick={() => handleClone(scenario)}
                          className="p-1 hover:bg-green-100 rounded transition"
                          title="Clone & Customize for your airline"
                        >
                          <Copy size={16} className="text-green-600" />
                        </button>
                      ) : (
                        <button
                          onClick={() => openEditModal(scenario)}
                          className="p-1 hover:bg-orca-elevate/10 rounded transition"
                          title="Edit"
                        >
                          <Edit size={16} className="text-orca-elevate" />
                        </button>
                      )}
                      {!isSystemScenario(scenario) && (
                        <button
                          onClick={() => handleDelete(scenario)}
                          className="p-1 hover:bg-red-100 rounded transition"
                          title="Delete"
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-steel/20">
              <h3 className="text-2xl font-bold text-navy">
                {editingScenario ? 'Edit Scenario' : 'Create Scenario'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      name,
                      ...(!editingScenario ? { code: generateCode(name) } : {}),
                    }));
                  }}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  placeholder="e.g. LOFT Two-Crew Scenario"
                />
              </div>

              {/* Code */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Code *</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate font-mono"
                  placeholder="loft_two_crew"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  placeholder="Brief description of this scenario"
                />
              </div>

              {/* Context Description */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Context Description
                </label>
                <p className="text-xs text-steel mb-1">
                  Environment context for transcription prompts
                </p>
                <textarea
                  value={form.context_description}
                  onChange={(e) => setForm({ ...form, context_description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  placeholder="e.g. Full-flight simulator session with two crew members and one instructor..."
                />
              </div>

              {/* Transcription Prompt Template */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">
                  Transcription Prompt Template (Handlebars)
                </label>
                <textarea
                  value={form.transcription_prompt}
                  onChange={(e) => setForm({ ...form, transcription_prompt: e.target.value })}
                  rows={10}
                  className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate font-mono text-sm"
                  placeholder="Enter Handlebars template for the transcription prompt..."
                />
                <div className="mt-1 text-xs text-steel">
                  <span className="font-semibold">Available variables:</span>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{speaker_roles}}}'}</code>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{speaker_snippets}}}'}</code>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{keyterms}}}'}</code>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{context_description}}}'}</code>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{scenario_name}}}'}</code>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{phase_context}}}'}</code>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{{debrief_terms}}}'}</code>
                  <br />
                  <span className="font-semibold">Conditionals:</span>{' '}
                  <code className="bg-steel/10 px-1 rounded">{'{{#if speaker_snippets}}...{{/if}}'}</code>
                </div>
              </div>

              {/* Speaker Roles */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Speaker Roles</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {PREDEFINED_ROLES.filter((r) => !form.speaker_roles.includes(r)).map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => addRole(role)}
                      className="px-2 py-1 bg-steel/10 text-steel rounded text-xs hover:bg-steel/20 transition"
                    >
                      + {role}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={customRoleInput}
                    onChange={(e) => setCustomRoleInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addRole(customRoleInput); setCustomRoleInput(''); }
                    }}
                    className="flex-1 px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
                    placeholder="Custom role name..."
                  />
                  <button
                    type="button"
                    onClick={() => { addRole(customRoleInput); setCustomRoleInput(''); }}
                    className="px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition text-sm"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.speaker_roles.map((role) => (
                    <span
                      key={role}
                      className="flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-800 rounded text-sm"
                    >
                      {role}
                      <button
                        type="button"
                        onClick={() => removeRole(role)}
                        className="text-sky-600 hover:text-red-600 ml-1"
                      >
                        <XCircle size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Speaker Role Descriptions */}
              {form.speaker_roles.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Speaker Role Descriptions
                  </label>
                  <div className="space-y-3">
                    {form.speaker_roles.map((role) => (
                      <div key={role}>
                        <label className="block text-xs font-medium text-steel mb-1">{role}</label>
                        <textarea
                          value={form.speaker_role_descriptions[role] || ''}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              speaker_role_descriptions: {
                                ...prev.speaker_role_descriptions,
                                [role]: e.target.value,
                              },
                            }))
                          }
                          rows={2}
                          className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
                          placeholder={`Description for ${role}...`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Speaker count */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Min Speakers</label>
                  <input
                    type="number"
                    min={1}
                    value={form.min_speakers}
                    onChange={(e) => setForm({ ...form, min_speakers: Math.max(1, Number(e.target.value)) })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Max Speakers</label>
                  <input
                    type="number"
                    min={form.min_speakers}
                    value={form.max_speakers}
                    onChange={(e) => setForm({ ...form, max_speakers: Math.max(form.min_speakers, Number(e.target.value)) })}
                    className="w-full px-4 py-2 bg-white text-navy border border-steel/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-orca-elevate"
                  />
                </div>
              </div>

              {/* Session Types */}
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Session Types</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sessionTypes.map((st) => (
                    <label key={st.id} className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.session_type_ids.includes(st.id)}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            session_type_ids: e.target.checked
                              ? [...prev.session_type_ids, st.id]
                              : prev.session_type_ids.filter((id) => id !== st.id),
                          }));
                        }}
                        className="rounded border-steel/30 text-orca-elevate focus:ring-orca-elevate"
                      />
                      {st.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Active toggle */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-navy cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-steel/30 text-orca-elevate focus:ring-orca-elevate"
                  />
                  Active
                </label>
              </div>
            </div>

            {/* Modal footer */}
            <div className="p-6 border-t border-steel/20 flex justify-end gap-3">
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="px-4 py-2 bg-steel/10 text-navy rounded-lg hover:bg-steel/20 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition disabled:opacity-50"
              >
                {saving && <Loader className="animate-spin" size={16} />}
                {editingScenario ? 'Update Scenario' : 'Create Scenario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
